@use "../../styles/breakpoint.module.scss" as *;

.root {
    composes: grid from "../../styles/layout.module.scss";
    grid-column: 1 / -1;
    overflow: hidden;
    user-select: none;
}

.stage {
    grid-column: 1 / -1;
    position: relative;
    height: clamp(525px, 45vw, 600px);

    // Distance from the bottom of the stage to the shared orbit pivot.
    // Larger values = gentler arc
    --orbit-extension: clamp(450px, 72vw, 1000px);

    @media ($below-mobile) {
        height: clamp(400px, 85vw, 460px);

        --orbit-extension: clamp(425px, 60vw, 700px);
    }
}

// ── Orbit (single rotating ring containing every card) ──

.orbit {
    position: absolute;
    inset: 0;
    transform-origin: 50% calc(100% + var(--orbit-extension));
    transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform;

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
}

// ── Card Slot (anchors a card on the orbit at --card-angle) ──

.cardSlot {
    position: absolute;
    left: 50%;
    bottom: 0;
    width: clamp(400px, 33vw, 450px);
    height: clamp(525px, 45vw, 600px);
    // Pivot point in element coords lands at the same page point as the
    // orbit's pivot, so every slot rotates around the shared center.
    transform-origin: 50% calc(100% + var(--orbit-extension));
    transform: translateX(-50%) rotate(var(--card-angle, 0deg));

    &[data-navigable] {
        cursor: pointer;
    }

    @media ($below-mobile) {
        width: clamp(290px, 75vw, 300px);
        height: clamp(400px, 85vw, 460px);
    }
}

// ── Flip Container ──

.flipContainer {
    composes: themed from "../../styles/themes.module.scss";
    width: 100%;
    height: 100%;
    position: relative;
    transform-style: preserve-3d;
    transition: transform 0.6s ease;
    border-radius: var(--border-radius-md);
    box-shadow: var(--box-shadow-3);

    &[data-flipped] {
        transform: rotateY(180deg);
    }

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
}

// ── Front Face ──

.front {
    position: absolute;
    inset: 0;
    backface-visibility: hidden;
    display: flex;
    flex-direction: column;
    background: var(--color-primary);
    color: var(--color-text);
    padding: var(--spacer-32);
    gap: var(--spacer-32);
    border-radius: inherit;
    overflow: hidden;
}

.cardHeader {
    display: flex;
    flex-direction: column;
    gap: var(--spacer-4);
}

.eyebrow {
    composes: fontST16 from "../../styles/typography.module.scss";
    font-weight: 800;
    text-transform: uppercase;
}

.cardTitle {
    composes: fontST24 from "../../styles/typography.module.scss";
    font-weight: 800;
}

.divider {
    height: 1px;
    background: var(--color-text);
    margin-top: var(--spacer-16);
}

.cardBody {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.frontBody {
    composes: fontSH32 from "../../styles/typography.module.scss";
    flex: 1;
}

.ctaWrapper {
    display: flex;
}

// ── Back Face ──

.back {
    position: absolute;
    inset: 0;
    backface-visibility: hidden;
    transform: rotateY(180deg);
    display: flex;
    flex-direction: column;
    background: var(--white);
    color: var(--color-primary);
    padding: var(--spacer-32);
    gap: var(--spacer-32);
    border-radius: inherit;
    overflow: hidden;
    cursor: pointer;
}

.backHeader {
    display: flex;
    flex-direction: column;
    gap: var(--spacer-4);
}

.backEyebrow {
    composes: fontST16 from "../../styles/typography.module.scss";
    font-weight: 800;
    text-transform: uppercase;
}

.backTitle {
    composes: fontST24 from "../../styles/typography.module.scss";
    font-weight: 800;
}

.backDivider {
    height: 1px;
    background: var(--color-primary);
    margin-top: var(--spacer-16);
}

.backContent {
    flex: 1;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-32);
}

.backSection {
    display: flex;
    flex-direction: column;
    gap: var(--spacer-8);
}

.backLabel {
    composes: fontST14 from "../../styles/typography.module.scss";
    font-weight: 800;
    text-transform: uppercase;
}

.backBody {
    composes: fontP16 from "../../styles/typography.module.scss";
}

.backRichText {
    composes: fontP16 from "../../styles/typography.module.scss";
    color: var(--color-primary);

    ul {
        margin: 0;
    }

    li {
        margin: 0;

        &::marker {
            color: var(--color-primary);
        }
    }
}

// ── Controls ──

.controls {
    grid-column: content;
    display: flex;
    justify-content: center;
    gap: var(--spacer-8);
    margin-top: var(--spacer-24);
    margin-bottom: var(--spacer-80);

    > :first-child {
        transform: rotate(180deg);
    }
}
